import React, { useState } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Button,
	Collapse,
	FormGroup,
	FormText,
	Label
} from 'reactstrap';

import { FormField, FIELD_TYPES, FORM_TYPES } from '@techchange/components';
import { messages } from '../../i18n';

import { formatIntlResourceType, isResourceContent } from '../../utils';

import ContentSettingsEditor from './ContentSettingsEditor';
import DefaultSettingsEditor from './DefaultSettingsEditor';

const propTypes = {

	/**
	 * The resource to display.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID for the resource.
		 */
		id: PropTypes.string,

		/**
		 * The type of the resource.
		 */
		type: PropTypes.string,

		/**
		 * The open date for the resource.
		 */
		open_date: PropTypes.string,

		/**
		 * The ID for the default child resource to display.
		 */
		default_child_id: PropTypes.string,

		/**
		 * Flag indicating if the title should be shown.
		 */
		show_title: PropTypes.bool,

		/**
		 * The metadata for the resource.
		 */
		data: PropTypes.shape({

			/**
			 * The associated form ID (if any) for the resource.
			 */
			form_id: PropTypes.string,

		}),

		/**
		 * The children resources (if any).
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The child resource ID.
			 */
			id: PropTypes.string,

		})),

	}),

	/**
	 * The associated form (if any) for the resource.
	 */
	form: PropTypes.shape({

		/**
		 * The form ID.
		 */
		id: PropTypes.string,

		/**
		 * The form type.
		 */
		type: PropTypes.oneOf([
			FORM_TYPES.FORM,
			FORM_TYPES.POLL,
			FORM_TYPES.QUIZ,
		]).isRequired,

		/**
		 * True, if learners are allowed to edit form entry submissions.
		 */
		allow_editing_submissions: PropTypes.bool,

		/**
		 * True, if learners are allowed to submit multiple form entries.
		 */
		allow_multiple_submissions: PropTypes.bool,

		/**
		 * The percentage grade needed to pass the quiz (if any).
		 */
		quiz_result_pass_percentage: PropTypes.number,

		/**
		 * True, if learners are allowed to see the correct answers after their submission.
		 */
		quiz_result_show_correct_answers: PropTypes.bool,

	}),

	/**
	 * The current user's timezone.
	 */
	userTimezone: PropTypes.string.isRequired,

	/**
	 * Function to handle cancel.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * Function to handle deleting the resource.
	 */
	onDelete: PropTypes.func.isRequired,

	/**
	 * Function to handle form submission.
	 */
	onSave: PropTypes.func.isRequired,

};

const ResourceSettingsEditor = (props) => {

	const {
		resource,
		form,
		userTimezone,
		onCancel,
		onDelete,
		onSave,
	} = props;

	const intl = useIntl();

	// Initialize the form data
	const [data, setData] = useState({
		open_date: resource.open_date ? moment(resource.open_date).tz(userTimezone) : null,
		default_child_id: resource.default_child_id || '',
		show_title: resource.show_title || false,
		allow_editing_submissions: form?.allow_editing_submissions || false,
		allow_multiple_submissions: form?.allow_multiple_submissions || false,
		quiz_result_pass_percentage:
			form?.type === FORM_TYPES.QUIZ ? form.quiz_result_pass_percentage : null,
		quiz_result_show_correct_answers:
			form?.type === FORM_TYPES.QUIZ ? form.quiz_result_show_correct_answers : null,
	});

	const [showAdvanced, setShowAdvanced] = useState(false);

	// Handle changes to the form data
	const handleInputChange = (name, value) => setData({ ...data, [name]: value });

	// Handle submitting the form
	const handleSubmit = (event) => {

		if (event) {
			event.preventDefault();
		}

		const dataToSubmit = { ...data };

		if (dataToSubmit.open_date) {
			dataToSubmit.open_date = dataToSubmit.open_date.tz('GMT');
		}

		onSave(dataToSubmit);

	};

	// Check if the resource is a piece of content
	// i.e. embed, image, slide, video, etc.
	const isContent = isResourceContent(resource);

	// Get the internationalized version of the resource type
	const intlResourceType = formatIntlResourceType(intl, resource.type);

	return (
		<form
			className="tc-resource-settings-editor"
			onSubmit={handleSubmit}>
			{isContent &&
				<ContentSettingsEditor
					data={data}
					onInputChange={handleInputChange}
					{...props} />
			}
			{!isContent &&
				<DefaultSettingsEditor
					data={data}
					onInputChange={handleInputChange}
					{...props} />
			}
			<FormField
				type={FIELD_TYPES.DATETIME}
				name="open_date"
				value={data.open_date}
				label={intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_FIELD_LABEL_TEXT_OPEN_DATE)}
				defaultTimezone={userTimezone}
				isEditing
				onChange={handleInputChange} />
			<FormGroup>
				<Label>
					<FormattedMessage
						{...messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_DELETE_LABEL_TEXT}
						values={{ type: intlResourceType }} />
				</Label>
				<FormText tag="div">
					<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_DELETE_CONFIRMATION_TEXT} />
				</FormText>
				<Button
					className="tc-delete-button"
					color="danger"
					onClick={onDelete}>
					<FormattedMessage
						{...messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_DELETE_BUTTON}
						values={{ type: intlResourceType }} />
				</Button>
			</FormGroup>
			<FormGroup>
				<Button
					color="link"
					onClick={() => setShowAdvanced(!showAdvanced)}>
					{showAdvanced
						? <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_HIDE_ADVANCED_OPTIONS} />
						: <FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_SHOW_ADVANCED_OPTIONS} />
					}
				</Button>
				<Collapse isOpen={showAdvanced}>
					<div className="pt-2">
						<b>
							<FormattedMessage
								{...messages.TC_COMPONENTS_RESOURCE_SETTINGS_MODAL_RESOURCE_ID_LABEL_TEXT} />
						</b>
						{' '}
						<span>
							{resource.id}
						</span>
					</div>
				</Collapse>
			</FormGroup>
			<FormGroup>
				<Button
					color="primary"
					type="submit">
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
				</Button>
				<Button
					className="ms-3"
					color="neutral"
					type="button"
					outline
					onClick={onCancel}>
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
				</Button>
			</FormGroup>
		</form>
	);

};

ResourceSettingsEditor.propTypes = propTypes;

export default ResourceSettingsEditor;
